import React, { useState, useEffect } from 'react';
import { invoiceAPI, settingsAPI, bankAccountAPI, ksefAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import DocumentUpload from '../components/DocumentUpload';
import { getInvoiceTemplates, previewInvoiceNumber } from '../services/invoiceTemplateService';
import { useTranslation } from 'react-i18next';

const Invoices = () => {
  const { t } = useTranslation();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [ksefExportingId, setKsefExportingId] = useState(null);
  const [ksefImporting, setKsefImporting] = useState(false);
  const [correctionFor, setCorrectionFor] = useState(null);
  const [correctionReason, setCorrectionReason] = useState('');
  const [filters, setFilters] = useState({
    type: '',
    status: '',
    ownership: 'all',
    companyName: ''
  });
  const [templates, setTemplates] = useState([]);
  const [previewedNumber, setPreviewedNumber] = useState('');
  const [defaultPaymentDays, setDefaultPaymentDays] = useState(14); // Default payment period
  const [defaultVatRate, setDefaultVatRate] = useState(23); // Default VAT rate
  const [defaultLocation, setDefaultLocation] = useState(''); // Default invoice location from CompanySettings
  const [ksefDemo, setKsefDemo] = useState(null);
  const [bankAccounts, setBankAccounts] = useState([]);

  const { user } = useAuth();
  const canEdit = user?.role === 'super_admin' || user?.role === 'admin' || user?.role === 'accountant';

  const categories = [
    'consulting', 'software_development', 'design', 'marketing',
    'training', 'maintenance', 'subscription', 'product_sale',
    'salary', 'rent', 'utilities', 'supplies', 'equipment',
    'travel', 'professional_services', 'insurance', 'taxes', 'other'
  ];

  // Helper function: Get today's date in YYYY-MM-DD format
  const getTodayDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  // Helper function: Add days to a date
  const addDays = (dateString, days) => {
    const date = new Date(dateString);
    date.setDate(date.getDate() + days);
    return date.toISOString().split('T')[0];
  };

  // Helper function: Get last day of month for a given date
  const getLastDayOfMonth = (dateString) => {
    const date = new Date(dateString);
    const lastDay = new Date(date.getFullYear(), date.getMonth() + 1, 0);
    return lastDay.toISOString().split('T')[0];
  };

  // Helper function: Format date for display as DD/MM/YYYY
  const formatDateDisplay = (dateString) => {
    if (!dateString) return '';
    // Parse YYYY-MM-DD format directly to avoid timezone issues
    const [year, month, day] = dateString.split('T')[0].split('-');
    return `${day}/${month}/${year}`;
  };

  const [formData, setFormData] = useState({
    templateId: '',
    invoiceNumber: '',
    type: 'incoming',
    category: 'other',
    pdfLanguage: 'pl',
    clientName: '',
    clientEmail: '',
    clientAddress: '',
    clientNip: '',
    location: '',
    description: '',
    unitPrice: '',
    quantity: '1',
    vatRate: '',
    taxDeductiblePercentage: '100',
    amount: '',
    taxAmount: '0',
    totalAmount: '',
    currency: 'PLN',
    status: 'draft',
    issueDate: '',
    dueDate: '',
    serviceDate: '',
    paymentMethod: 'bank_transfer',
    notes: '',
    reverseCharge: false,
    applyTaxRounding: false,
    attachmentPath: '',
    attachmentOriginalName: '',
    bankAccountId: '',
    items: [{
      description: '',
      unitPrice: '',
      quantity: '1',
      vatRate: '',
      pkwiu: '-',
      unit: 'godz.'
    }]
  });

  useEffect(() => {
    fetchInvoices();
    fetchTemplates();
    fetchCompanySettings();
    fetchBankAccounts();
  }, [filters]);

  const fetchBankAccounts = async () => {
    try {
      const response = await bankAccountAPI.getAll();
      setBankAccounts(response.data);
    } catch (err) {
      console.error('Failed to load bank accounts:', err);
    }
  };

  const fetchCompanySettings = async () => {
    try {
      const response = await settingsAPI.getCompanySettings();
      const settings = response.data.settings;
      if (settings?.defaultPaymentDays) {
        setDefaultPaymentDays(settings.defaultPaymentDays);
      }
      if (settings?.defaultVatRate !== undefined && settings?.defaultVatRate !== null) {
        setDefaultVatRate(settings.defaultVatRate);
      }
      if (settings?.invoiceLocation) {
        setDefaultLocation(settings.invoiceLocation);
      }
      if (settings?.ksefKeyTest || settings?.ksefKey) {
        setKsefDemo(settings.ksefDemo !== undefined ? settings.ksefDemo : true);
      }
    } catch (err) {
      console.error('Failed to load company settings:', err);
      // Keep using default values if fetch fails
    }
  };

  // Auto-populate dates and VAT rate when opening form for new invoice
  useEffect(() => {
    if (showForm && !editingId) {
      const today = getTodayDate();
      const dueDate = addDays(today, defaultPaymentDays);
      const serviceDate = getLastDayOfMonth(today);
      setFormData(prev => ({
        ...prev,
        issueDate: today,
        dueDate: dueDate,
        serviceDate: serviceDate,
        vatRate: prev.vatRate || defaultVatRate,
        location: prev.location || defaultLocation
      }));
    }
  }, [showForm, editingId, defaultPaymentDays, defaultVatRate, defaultLocation]);

  const fetchTemplates = async () => {
    try {
      const data = await getInvoiceTemplates({ isActive: true });
      setTemplates(data.templates || []);
    } catch (err) {
      console.error('Failed to load templates:', err);
    }
  };

  const fetchInvoices = async () => {
    try {
      setLoading(true);
      const params = {};
      if (filters.type) params.type = filters.type;
      if (filters.status) params.status = filters.status;
      if (filters.ownership) params.ownership = filters.ownership;

      const response = await invoiceAPI.getAll(params);

      // Apply client-side company name filter if specified
      let filteredInvoices = response.data.invoices || [];
      if (filters.companyName) {
        const searchTerm = filters.companyName.toLowerCase();
        filteredInvoices = filteredInvoices.filter(invoice => {
          const companyName = invoice.user?.companySettings?.companyName || '';
          return companyName.toLowerCase().includes(searchTerm);
        });
      }

      setInvoices(filteredInvoices);
      setError('');
    } catch (err) {
      setError(t('common.error'));
      console.error('Invoices error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    setFilters({
      ...filters,
      [e.target.name]: e.target.value
    });
  };

  // Helper function: Convert DD/MM/YYYY to YYYY-MM-DD
  const convertToISODate = (ddmmyyyy) => {
    if (!ddmmyyyy || ddmmyyyy.length !== 10) return '';
    const [day, month, year] = ddmmyyyy.split('/');
    if (!day || !month || !year) return '';
    return `${year}-${month}-${day}`;
  };

  // Helper function: Convert YYYY-MM-DD to DD/MM/YYYY for display
  const convertToDisplayDate = (isoDate) => {
    if (!isoDate) return '';
    return formatDateDisplay(isoDate);
  };

  // Calculate totals from all line items
  const calculateTotalsFromItems = (items, applyRounding = false) => {
    let totalNet = 0;
    let totalVat = 0;

    items.forEach(item => {
      const unitPrice = parseFloat(item.unitPrice) || 0;
      const quantity = parseFloat(item.quantity) || 0;
      const vatRate = parseFloat(item.vatRate) || 0;
      const itemNet = unitPrice * quantity;
      const itemVat = (itemNet * vatRate) / 100;
      totalNet += itemNet;
      totalVat += itemVat;
    });

    // Conditionally apply Polish tax rounding rule based on applyRounding flag
    let finalTaxAmount = totalVat;
    let finalGrossAmount = totalNet + totalVat;

    if (applyRounding) {
      // Round GROSS total to full zlotys and back-calculate tax
      // Amounts ending in less than 50 groszy are rounded down, 50+ rounded up
      const roundedGross = Math.round(finalGrossAmount);
      finalTaxAmount = roundedGross - totalNet;
      finalGrossAmount = roundedGross;
    }

    return {
      amount: totalNet.toFixed(2),
      taxAmount: finalTaxAmount.toFixed(2),
      totalAmount: finalGrossAmount.toFixed(2)
    };
  };

  // Handle item change
  const handleItemChange = (index, field, value) => {
    setFormData(prev => {
      const newItems = [...prev.items];
      newItems[index] = { ...newItems[index], [field]: value };

      // Recalculate totals
      const totals = calculateTotalsFromItems(newItems, prev.applyTaxRounding);

      return {
        ...prev,
        items: newItems,
        ...totals
      };
    });
  };

  // Add new line item
  const addLineItem = () => {
    setFormData(prev => {
      // Get default description from template if selected
      const template = templates.find(t => t.id === prev.templateId);
      const defaultDescription = template?.defaultDescription || '';

      return {
        ...prev,
        items: [...prev.items, {
          description: defaultDescription,
          unitPrice: '',
          quantity: '1',
          vatRate: prev.items[0]?.vatRate || '',
          pkwiu: '-',
          unit: 'godz.'
        }]
      };
    });
  };

  // Remove line item
  const removeLineItem = (index) => {
    if (formData.items.length === 1) return; // Keep at least one item
    setFormData(prev => {
      const newItems = prev.items.filter((_, i) => i !== index);
      const totals = calculateTotalsFromItems(newItems, prev.applyTaxRounding);
      return {
        ...prev,
        items: newItems,
        ...totals
      };
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const updated = { ...prev, [name]: value };

      // Auto-calculate amounts based on unit price, quantity, and VAT rate (legacy single-item support)
      if (name === 'unitPrice' || name === 'quantity' || name === 'vatRate') {
        const unitPrice = parseFloat(name === 'unitPrice' ? value : updated.unitPrice) || 0;
        const quantity = parseFloat(name === 'quantity' ? value : updated.quantity) || 0;
        const vatRate = parseFloat(name === 'vatRate' ? value : updated.vatRate) || 0;

        // Calculate net amount (netto)
        const netAmount = unitPrice * quantity;
        updated.amount = netAmount.toFixed(2);

        // Calculate tax amount
        const taxAmount = (netAmount * vatRate) / 100;
        updated.taxAmount = taxAmount.toFixed(2);

        // Calculate total amount (brutto)
        updated.totalAmount = (netAmount + taxAmount).toFixed(2);
      }

      // Fallback: Auto-calculate total amount if amount or taxAmount changed directly
      if (name === 'amount' || name === 'taxAmount') {
        const amount = parseFloat(name === 'amount' ? value : updated.amount) || 0;
        const tax = parseFloat(name === 'taxAmount' ? value : updated.taxAmount) || 0;
        updated.totalAmount = (amount + tax).toFixed(2);
      }

      // Auto-update dueDate and serviceDate when issueDate changes
      if (name === 'issueDate' && value) {
        updated.dueDate = addDays(value, defaultPaymentDays);
        updated.serviceDate = getLastDayOfMonth(value);
      }

      return updated;
    });
  };

  const handleDateChange = (e) => {
    const { name, value } = e.target;

    // Auto-format as user types: add slashes after day and month
    let formattedValue = value.replace(/\D/g, ''); // Remove non-digits
    if (formattedValue.length >= 2) {
      formattedValue = formattedValue.slice(0, 2) + '/' + formattedValue.slice(2);
    }
    if (formattedValue.length >= 5) {
      formattedValue = formattedValue.slice(0, 5) + '/' + formattedValue.slice(5, 9);
    }

    // Update display value
    setFormData(prev => {
      const updated = { ...prev, [`${name}_display`]: formattedValue };

      // If we have a complete date (DD/MM/YYYY), convert to ISO and store
      if (formattedValue.length === 10) {
        const isoDate = convertToISODate(formattedValue);
        if (isoDate) {
          updated[name] = isoDate;

          // Auto-update dueDate and serviceDate when issueDate changes
          if (name === 'issueDate') {
            const dueDate = addDays(isoDate, defaultPaymentDays);
            const serviceDate = getLastDayOfMonth(isoDate);
            updated.dueDate = dueDate;
            updated.serviceDate = serviceDate;
            updated.dueDate_display = convertToDisplayDate(dueDate);
            updated.serviceDate_display = convertToDisplayDate(serviceDate);
          }
        }
      } else {
        updated[name] = '';
      }

      return updated;
    });
  };

  const handleTemplateChange = async (e) => {
    const templateId = e.target.value;
    setFormData(prev => ({ ...prev, templateId }));

    if (!templateId) {
      setPreviewedNumber('');
      return;
    }

    const template = templates.find(t => t.id === templateId);

    // Preview invoice number
    try {
      const data = await previewInvoiceNumber(templateId);
      setPreviewedNumber(data.invoiceNumber);

      // Auto-fill from template
      setFormData(prev => ({
        ...prev,
        templateId,
        type: template.type,
        clientName: template.clientName || prev.clientName,
        clientEmail: template.clientEmail || prev.clientEmail,
        clientAddress: template.clientAddress || prev.clientAddress,
        clientNip: template.clientNip || prev.clientNip,
        description: template.defaultDescription || prev.description,
        currency: template.currency || prev.currency,
        vatRate: template.taxRate !== undefined && template.taxRate !== null ? template.taxRate : (prev.vatRate || defaultVatRate),
        notes: template.notes || prev.notes,
        items: prev.items.map(item => ({
          ...item,
          description: template.defaultDescription || item.description,
          vatRate: template.taxRate !== undefined && template.taxRate !== null ? template.taxRate : item.vatRate
        }))
      }));
    } catch (err) {
      console.error('Failed to preview invoice number:', err);
    }
  };

  const resetForm = () => {
    setFormData({
      templateId: '',
      invoiceNumber: '',
      type: 'incoming',
      category: 'other',
      pdfLanguage: 'pl',
      clientName: '',
      clientEmail: '',
      clientAddress: '',
      clientNip: '',
      location: '',
      description: '',
      unitPrice: '',
      quantity: '1',
      vatRate: '',
      taxDeductiblePercentage: '100',
      amount: '',
      taxAmount: '0',
      totalAmount: '',
      currency: 'PLN',
      status: 'draft',
      issueDate: '',
      dueDate: '',
      serviceDate: '',
      paymentMethod: 'bank_transfer',
      notes: '',
      reverseCharge: false,
      applyTaxRounding: false,
      attachmentPath: '',
      attachmentOriginalName: '',
      bankAccountId: '',
      items: [{
        description: '',
        unitPrice: '',
        quantity: '1',
        vatRate: '',
        pkwiu: '-',
        unit: 'godz.'
      }]
    });
    setEditingId(null);
    setShowForm(false);
    setPreviewedNumber('');
    setCorrectionFor(null);
    setCorrectionReason('');
  };

  const handleOCRDataExtracted = (extractedData, fileInfo) => {
    console.log('[Frontend] OCR data received from backend:');
    console.log('extractedData:', JSON.stringify(extractedData, null, 2));
    console.log('fileInfo:', JSON.stringify(fileInfo, null, 2));

    // Map AI payment method to database ENUM values
    const mapPaymentMethod = (aiMethod) => {
      const methodMap = {
        'card': 'credit_card',
        'credit card': 'credit_card',
        'debit card': 'credit_card',
        'bank transfer': 'bank_transfer',
        'transfer': 'bank_transfer',
        'cash': 'cash',
        'check': 'check',
        'cheque': 'check'
      };

      if (!aiMethod) return 'bank_transfer'; // default

      const normalized = aiMethod.toLowerCase().trim();
      return methodMap[normalized] || 'bank_transfer';
    };

    // Auto-fill form with OCR extracted data
    // Map AI-extracted field names to form field names
    const updatedFormData = {
      ...formData,
      // Invoice details
      invoiceNumber: extractedData.invoiceNumber || formData.invoiceNumber,
      issueDate: extractedData.issueDate || formData.issueDate,
      dueDate: extractedData.dueDate || formData.dueDate,
      serviceDate: extractedData.serviceDate || extractedData.issueDate || formData.serviceDate,

      // Set invoice type as 'outgoing' (incoming invoice / bought goods)
      type: 'outgoing',

      // Set status as 'paid' and paidDate to issueDate
      status: 'paid',
      paidDate: extractedData.issueDate || formData.issueDate,

      // Client/Buyer information
      clientName: extractedData.clientName || formData.clientName,
      clientAddress: extractedData.clientAddress || formData.clientAddress,
      clientNip: extractedData.clientNip || formData.clientNip,
      clientEmail: extractedData.clientEmail || formData.clientEmail,

      // Financial details - map AI field names to form field names
      amount: extractedData.totalNet || extractedData.amount || formData.amount,
      taxAmount: extractedData.totalVat || extractedData.taxAmount || formData.taxAmount,
      totalAmount: extractedData.totalGross || extractedData.totalAmount || formData.totalAmount,

      // Payment details
      currency: extractedData.currency || formData.currency,
      paymentMethod: mapPaymentMethod(extractedData.paymentMethod),

      // Reverse charge flag
      reverseCharge: extractedData.reverseCharge || formData.reverseCharge,

      // Notes
      notes: extractedData.notes || formData.notes,

      // Attachment information from uploaded document
      attachmentPath: fileInfo?.filename || '',
      attachmentOriginalName: fileInfo?.originalName || '',

      // Line items - use AI extracted items if available
      items: extractedData.items && extractedData.items.length > 0
        ? extractedData.items.map(item => ({
            description: item.description || '',
            unitPrice: item.unitPrice || '',
            quantity: item.quantity || 1,
            vatRate: item.vatRate || 23,
            pkwiu: item.pkwiu || '-',
            unit: item.unit || 'szt'
          }))
        : formData.items
    };

    console.log('[Frontend] Mapped form data to be set:');
    console.log('updatedFormData:', JSON.stringify(updatedFormData, null, 2));

    setFormData(updatedFormData);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      // Remove legacy single-item fields since we're using items array now
      const { description, unitPrice, quantity, vatRate, ...invoiceData } = formData;

      // Convert empty string to null for UUID fields (PostgreSQL requirement)
      if (invoiceData.templateId === '') {
        invoiceData.templateId = null;
      }

      if (correctionFor) {
        if (!correctionReason.trim()) {
          setError(t('invoices.correctionReasonRequired'));
          return;
        }
        await invoiceAPI.createCorrection(correctionFor.id, { ...invoiceData, correctionReason });
      } else if (editingId) {
        await invoiceAPI.update(editingId, invoiceData);
      } else {
        await invoiceAPI.create(invoiceData);
      }

      resetForm();
      fetchInvoices();
    } catch (err) {
      setError(err.response?.data?.error || t('invoices.failedToSave'));
    }
  };

  const handleEdit = (invoice) => {
    setFormData({
      templateId: invoice.templateId || '',
      invoiceNumber: invoice.invoiceNumber,
      type: invoice.type,
      category: invoice.category || 'other',
      pdfLanguage: invoice.pdfLanguage || 'pl',
      clientName: invoice.clientName,
      clientEmail: invoice.clientEmail || '',
      clientAddress: invoice.clientAddress || '',
      clientNip: invoice.clientNip || '',
      location: invoice.location || defaultLocation,
      description: invoice.description,
      unitPrice: invoice.unitPrice || '',
      quantity: invoice.quantity || '1',
      vatRate: invoice.vatRate || '',
      taxDeductiblePercentage: invoice.taxDeductiblePercentage || '100',
      amount: invoice.amount,
      taxAmount: invoice.taxAmount,
      totalAmount: invoice.totalAmount,
      currency: invoice.currency,
      status: invoice.status,
      issueDate: invoice.issueDate,
      dueDate: invoice.dueDate || '',
      serviceDate: invoice.serviceDate || '',
      paymentMethod: invoice.paymentMethod || 'bank_transfer',
      notes: invoice.notes || '',
      reverseCharge: invoice.reverseCharge || false,
      applyTaxRounding: invoice.applyTaxRounding || false,
      attachmentPath: invoice.attachmentPath || '',
      attachmentOriginalName: invoice.attachmentOriginalName || '',
      bankAccountId: invoice.bankAccountId || '',
      items: invoice.items && invoice.items.length > 0
        ? invoice.items.map(item => ({
            description: item.description,
            unitPrice: item.unitPrice,
            quantity: item.quantity,
            vatRate: item.vatRate,
            pkwiu: item.pkwiu || '-',
            unit: item.unit || 'godz.'
          }))
        : [{
            description: invoice.description || '',
            unitPrice: invoice.unitPrice || '',
            quantity: invoice.quantity || '1',
            vatRate: invoice.vatRate || '',
            pkwiu: '-',
            unit: 'godz.'
          }]
    });
    setEditingId(invoice.id);
    setShowForm(true);
  };

  const handleCreateCorrection = (invoice) => {
    const today = new Date().toISOString().split('T')[0];
    setFormData({
      templateId: '',
      invoiceNumber: '',
      type: invoice.type,
      category: invoice.category || 'other',
      pdfLanguage: invoice.pdfLanguage || 'pl',
      clientName: invoice.clientName,
      clientEmail: invoice.clientEmail || '',
      clientAddress: invoice.clientAddress || '',
      clientNip: invoice.clientNip || '',
      location: invoice.location || defaultLocation,
      description: invoice.description,
      unitPrice: invoice.unitPrice || '',
      quantity: invoice.quantity || '1',
      vatRate: invoice.vatRate || '',
      taxDeductiblePercentage: invoice.taxDeductiblePercentage || '100',
      amount: invoice.amount,
      taxAmount: invoice.taxAmount,
      totalAmount: invoice.totalAmount,
      currency: invoice.currency,
      status: 'draft',
      issueDate: today,
      dueDate: '',
      serviceDate: invoice.serviceDate || '',
      paymentMethod: invoice.paymentMethod || 'bank_transfer',
      notes: invoice.notes || '',
      reverseCharge: invoice.reverseCharge || false,
      applyTaxRounding: invoice.applyTaxRounding || false,
      attachmentPath: '',
      attachmentOriginalName: '',
      bankAccountId: invoice.bankAccountId || '',
      items: invoice.items && invoice.items.length > 0
        ? invoice.items.map(item => ({
            description: item.description,
            unitPrice: item.unitPrice,
            quantity: item.quantity,
            vatRate: item.vatRate,
            pkwiu: item.pkwiu || '-',
            unit: item.unit || 'godz.'
          }))
        : [{
            description: invoice.description || '',
            unitPrice: invoice.unitPrice || '',
            quantity: invoice.quantity || '1',
            vatRate: invoice.vatRate || '',
            pkwiu: '-',
            unit: 'godz.'
          }]
    });
    setCorrectionFor(invoice);
    setCorrectionReason('');
    setEditingId(null);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('invoices.deleteConfirm'))) {
      return;
    }

    try {
      await invoiceAPI.delete(id);
      fetchInvoices();
      setSuccess('');
    } catch (err) {
      setError(err.response?.data?.error || t('invoices.failedToDelete'));
    }
  };

  const handleExportToKsef = async (id) => {
    setKsefExportingId(id);
    setError('');
    setSuccess('');

    try {
      await ksefAPI.exportInvoice(id);
      setSuccess(t('invoices.ksefExported'));
    } catch (err) {
      setError(err.response?.data?.error || t('invoices.ksefExportFailed'));
    } finally {
      setKsefExportingId(null);
    }
  };

  const handleImportFromKsef = async () => {
    setKsefImporting(true);
    setError('');
    setSuccess('');

    try {
      const response = await ksefAPI.importInvoices();
      const { imported = 0, skipped = 0, fetched = 0 } = response.data || {};
      setSuccess(t('invoices.ksefImportSuccess', { imported, skipped, fetched }));
      fetchInvoices();
    } catch (err) {
      setError(err.response?.data?.error || t('invoices.ksefImportFailed'));
    } finally {
      setKsefImporting(false);
    }
  };

  const handleDownloadPDF = async (invoice) => {
    try {
      setError('');
      await invoiceAPI.downloadPDF(invoice.id);
    } catch (err) {
      setError(err.message || t('invoices.failedToDownload'));
      console.error('Download PDF error:', err);
    }
  };

  const handleDownloadAttachment = (invoice) => {
    if (!invoice.attachmentPath) {
      setError(t('invoices.noAttachmentFound'));
      return;
    }

    try {
      // Create download link for the original uploaded document
      const apiUrl = import.meta.env.VITE_API_URL || '/api';
      const fileUrl = `${apiUrl}/uploads/invoices/${invoice.attachmentPath}`;
      const link = document.createElement('a');
      link.href = fileUrl;
      link.download = invoice.attachmentOriginalName || invoice.attachmentPath;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      setError(err.message || t('invoices.failedToDownload'));
      console.error('Download attachment error:', err);
    }
  };

  const formatCurrency = (amount, currency = 'PLN') => {
    // Map currency codes to locales for better formatting
    const localeMap = {
      'PLN': 'pl-PL',
      'USD': 'en-US',
      'EUR': 'de-DE',
      'GBP': 'en-GB'
    };

    const locale = localeMap[currency] || 'pl-PL';

    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency || 'PLN'
    }).format(amount || 0);
  };

  return (
    <div className="max-w-[1920px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-4xl font-bold bg-gradient-to-r from-pastel-purple-600 to-pastel-blue-600 bg-clip-text text-transparent">Invoices</h1>
        {canEdit && (
          <div className="flex items-center gap-3">
            <button
              onClick={handleImportFromKsef}
              disabled={ksefImporting}
              className="bg-pastel-blue-600 hover:bg-pastel-blue-700 text-white font-medium py-2 px-6 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-0.5 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {ksefImporting ? t('common.loading') : t('invoices.importFromKsef')}
            </button>
            <button
              onClick={() => {
                if (showForm) {
                  // If form is open, close it (whether editing or creating)
                  resetForm();
                } else {
                  // Open form for creating new invoice
                  setShowForm(true);
                }
              }}
              className="bg-pastel-purple-600 hover:bg-pastel-purple-700 text-white font-medium py-2 px-6 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-0.5"
            >
              {showForm ? 'Cancel' : '+ New Invoice'}
            </button>
          </div>
        )}
      </div>

      {ksefDemo !== null && (
        <div className={`flex items-center gap-3 px-6 py-3 rounded-xl mb-6 border ${ksefDemo ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800 text-amber-800 dark:text-amber-200' : 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800 text-blue-800 dark:text-blue-200'}`}>
          <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span className="text-sm font-medium">
            {ksefDemo ? t('settings.ksefBannerDemo') : t('settings.ksefBannerProduction')}
          </span>
        </div>
      )}

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-800 dark:text-red-200 px-6 py-4 rounded-xl mb-6">
          {error}
        </div>
      )}
      {success && (
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-green-800 dark:text-green-200 px-6 py-4 rounded-xl mb-6">
          {success}
        </div>
      )}

      {showForm && canEdit && (
        <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-md shadow-xl rounded-2xl border border-pastel-purple-200 dark:border-gray-700 overflow-hidden mb-6">
          <div className="bg-gradient-to-r from-pastel-purple-500 to-pastel-blue-500 px-6 py-4 text-white font-bold text-xl">
            {correctionFor ? t('invoices.createCorrection') : editingId ? t('invoices.editInvoice') : t('invoices.createNewInvoice')}
          </div>
          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {correctionFor && (
              <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-amber-800 dark:text-amber-200 px-6 py-4 rounded-xl">
                <p className="font-medium">{t('invoices.correctionBanner', { number: correctionFor.invoiceNumber })}</p>
              </div>
            )}
            {correctionFor && (
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">{t('invoices.correctionReason')}*</label>
                <textarea
                  className="w-full px-4 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-pastel-purple-500 focus:border-transparent text-gray-900 dark:text-gray-100"
                  value={correctionReason}
                  onChange={(e) => setCorrectionReason(e.target.value)}
                  placeholder={t('invoices.correctionReasonPlaceholder')}
                  rows={2}
                  required
                />
              </div>
            )}
            {!editingId && !correctionFor && (
              <>
                <DocumentUpload
                  type="invoice"
                  onDataExtracted={handleOCRDataExtracted}
                />

                <div className="form-group mb-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">{t('invoices.invoiceTemplate')}</label>
                  <select
                    name="templateId"
                    className="w-full px-4 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-pastel-purple-500 focus:border-transparent text-gray-900 dark:text-gray-100"
                    value={formData.templateId}
                    onChange={handleTemplateChange}
                  >
                    <option value="">{t('invoices.selectTemplate')}</option>
                    {templates.map(template => (
                      <option key={template.id} value={template.id}>
                        {template.name} ({template.type})
                      </option>
                    ))}
                  </select>
                  {previewedNumber && (
                    <small style={{ color: '#28a745', marginTop: '0.5rem', display: 'block' }}>
                      {t('invoices.nextInvoiceNumber')} <strong>{previewedNumber}</strong>
                    </small>
                  )}
                </div>
              </>
            )}
            <div className="flex gap-2 mb-2">
              <div className="space-y-2" style={{ flex: 1 }}>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">{t('invoices.invoiceNumber')}{!formData.templateId && '*'}</label>
                <input
                  type="text"
                  name="invoiceNumber"
                  className="w-full px-4 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-pastel-purple-500 focus:border-transparent text-gray-900 dark:text-gray-100"
                  value={formData.invoiceNumber}
                  onChange={handleChange}
                  required={!formData.templateId}
                  placeholder={formData.templateId && !editingId ? t('invoices.autoGenerated') : ''}
                  disabled={!!formData.templateId && !editingId}
                />
                {formData.templateId && !editingId && (
                  <small style={{ color: '#666' }}>{t('invoices.willBeAutoGenerated')}</small>
                )}
              </div>

              <div className="space-y-2" style={{ flex: 1 }}>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">{t('invoices.type')}*</label>
                <select
                  name="type"
                  className="w-full px-4 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-pastel-purple-500 focus:border-transparent text-gray-900 dark:text-gray-100"
                  value={formData.type}
                  onChange={handleChange}
                  required
                >
                  <option value="incoming">{t('invoices.incoming')}</option>
                  <option value="outgoing">{t('invoices.outgoing')}</option>
                </select>
              </div>

              <div className="space-y-2" style={{ flex: 1 }}>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">{t('invoices.category')}*</label>
                <select
                  name="category"
                  className="w-full px-4 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-pastel-purple-500 focus:border-transparent text-gray-900 dark:text-gray-100"
                  value={formData.category}
                  onChange={handleChange}
                  required
                >
                  {categories.map(cat => (
                    <option key={cat} value={cat}>{t(`categories.${cat}`)}</option>
                  ))}
                </select>
              </div>

              <div className="space-y-2" style={{ flex: 1 }}>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">{t('invoices.pdfLanguage')}*</label>
                <select
                  name="pdfLanguage"
                  className="w-full px-4 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-pastel-purple-500 focus:border-transparent text-gray-900 dark:text-gray-100"
                  value={formData.pdfLanguage}
                  onChange={handleChange}
                  required
                >
                  <option value="pl">{t('invoices.pdfLanguagePL')}</option>
                  <option value="en">{t('invoices.pdfLanguageEN')}</option>
                  <option value="pl_en">{t('invoices.pdfLanguagePLEN')}</option>
                </select>
              </div>

              <div className="space-y-2" style={{ flex: 1 }}>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">{t('common.status')}*</label>
                <select
                  name="status"
                  className="w-full px-4 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-pastel-purple-500 focus:border-transparent text-gray-900 dark:text-gray-100"
                  value={formData.status}
                  onChange={handleChange}
                  required
                >
                  <option value="draft">{t('invoices.draft')}</option>
                  <option value="sent">{t('invoices.sent')}</option>
                  <option value="paid">{t('invoices.paid')}</option>
                  <option value="overdue">{t('invoices.overdue')}</option>
                  <option value="cancelled">{t('invoices.cancelled')}</option>
                </select>
              </div>
            </div>

            <div className="flex gap-2 mb-2">
              <div className="space-y-2" style={{ flex: 1 }}>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">{t('invoices.clientName')}*</label>
                <input
                  type="text"
                  name="clientName"
                  className="w-full px-4 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-pastel-purple-500 focus:border-transparent text-gray-900 dark:text-gray-100"
                  value={formData.clientName}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="space-y-2" style={{ flex: 1 }}>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">{t('invoices.clientEmail')}</label>
                <input
                  type="email"
                  name="clientEmail"
                  className="w-full px-4 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-pastel-purple-500 focus:border-transparent text-gray-900 dark:text-gray-100"
                  value={formData.clientEmail}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="flex gap-2 mb-2">
              <div className="space-y-2" style={{ flex: 1 }}>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">{t('invoices.clientAddress')}</label>
                <input
                  type="text"
                  name="clientAddress"
                  className="w-full px-4 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-pastel-purple-500 focus:border-transparent text-gray-900 dark:text-gray-100"
                  value={formData.clientAddress}
                  onChange={handleChange}
                />
              </div>

              <div className="space-y-2" style={{ flex: 1 }}>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">{t('invoices.clientNip')}</label>
                <input
                  type="text"
                  name="clientNip"
                  className="w-full px-4 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-pastel-purple-500 focus:border-transparent text-gray-900 dark:text-gray-100"
                  value={formData.clientNip}
                  onChange={handleChange}
                  placeholder="e.g. 123-456-78-90"
                />
              </div>

              <div className="space-y-2" style={{ flex: 1 }}>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">{t('invoices.location')}</label>
                <input
                  type="text"
                  name="location"
                  className="w-full px-4 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-pastel-purple-500 focus:border-transparent text-gray-900 dark:text-gray-100"
                  value={formData.location}
                  onChange={handleChange}
                />
              </div>
            </div>

            {/* Line Items Section */}
            <div className="space-y-4 border-t pt-4">
              <div className="flex justify-between items-center">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">{t('invoices.items')}*</label>
                <button
                  type="button"
                  onClick={addLineItem}
                  className="px-3 py-1 text-sm bg-pastel-blue-500 text-white rounded-lg hover:bg-pastel-blue-600"
                >
                  + {t('invoices.addItem')}
                </button>
              </div>

              {formData.items.map((item, index) => (
                <div key={index} className="border border-gray-300 dark:border-gray-600 rounded-lg p-4 space-y-3 bg-gray-50 dark:bg-gray-800">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('invoices.item')} #{index + 1}</span>
                    {formData.items.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeLineItem(index)}
                        className="text-red-600 hover:text-red-800 text-sm"
                      >
                        {t('invoices.removeItem')}
                      </button>
                    )}
                  </div>

                  <div className="space-y-2">
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400">{t('common.description')}*</label>
                    <textarea
                      className="w-full px-3 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-pastel-purple-500 focus:border-transparent text-gray-900 dark:text-gray-100 text-sm"
                      value={item.description}
                      onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                      rows="2"
                      required
                    />
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    <div className="space-y-2">
                      <label className="block text-xs font-medium text-gray-600 dark:text-gray-400">{t('invoices.unitPrice')}*</label>
                      <input
                        type="number"
                        step="0.01"
                        className="w-full px-3 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-pastel-purple-500 focus:border-transparent text-gray-900 dark:text-gray-100 text-sm"
                        value={item.unitPrice}
                        onChange={(e) => handleItemChange(index, 'unitPrice', e.target.value)}
                        required
                        placeholder="0.00"
                      />
                    </div>

                    <div className="space-y-2">
                      <label className="block text-xs font-medium text-gray-600 dark:text-gray-400">{t('invoices.quantity')}*</label>
                      <input
                        type="number"
                        step="0.01"
                        className="w-full px-3 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-pastel-purple-500 focus:border-transparent text-gray-900 dark:text-gray-100 text-sm"
                        value={item.quantity}
                        onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                        required
                        min="0"
                        placeholder="1"
                      />
                    </div>

                    <div className="space-y-2">
                      <label className="block text-xs font-medium text-gray-600 dark:text-gray-400">{t('invoices.vatRate')} (%)*</label>
                      <input
                        type="number"
                        step="0.01"
                        className="w-full px-3 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-pastel-purple-500 focus:border-transparent text-gray-900 dark:text-gray-100 text-sm"
                        value={item.vatRate}
                        onChange={(e) => handleItemChange(index, 'vatRate', e.target.value)}
                        required
                        min="0"
                        max="100"
                        placeholder="23"
                      />
                    </div>

                    <div className="space-y-2">
                      <label className="block text-xs font-medium text-gray-600 dark:text-gray-400">{t('invoices.unit')}</label>
                      <input
                        type="text"
                        className="w-full px-3 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-pastel-purple-500 focus:border-transparent text-gray-900 dark:text-gray-100 text-sm"
                        value={item.unit}
                        onChange={(e) => handleItemChange(index, 'unit', e.target.value)}
                        placeholder="godz."
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex gap-2 mb-2">
              <div className="space-y-2" style={{ flex: 1 }}>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">{t('invoices.amountNetto')}*</label>
                <input
                  type="number"
                  step="0.01"
                  name="amount"
                  className="w-full px-4 py-2 bg-gray-100 dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-gray-100"
                  value={formData.amount}
                  readOnly
                  disabled
                />
                <small className="text-gray-500 dark:text-gray-400">{t('invoices.calculatedUnitPrice')}</small>
              </div>

              <div className="space-y-2" style={{ flex: 1 }}>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">{t('invoices.taxAmount')}</label>
                <input
                  type="number"
                  step="0.01"
                  name="taxAmount"
                  className="w-full px-4 py-2 bg-gray-100 dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-gray-100"
                  value={formData.taxAmount}
                  readOnly
                  disabled
                />
                <small className="text-gray-500 dark:text-gray-400">{t('invoices.calculatedTax')}</small>
              </div>

              <div className="space-y-2" style={{ flex: 1 }}>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">{t('invoices.totalAmountBrutto')}*</label>
                <input
                  type="number"
                  step="0.01"
                  name="totalAmount"
                  className="w-full px-4 py-2 bg-gray-100 dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-gray-100"
                  value={formData.totalAmount}
                  readOnly
                  disabled
                />
                <small className="text-gray-500 dark:text-gray-400">{t('invoices.calculatedTotal')}</small>
              </div>
            </div>

            <div className="flex gap-2 mb-2">
              <div className="space-y-2" style={{ flex: 1 }}>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">{t('invoices.issueDate')}*</label>
                <input
                  type="date"
                  name="issueDate"
                  className="w-full px-4 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-pastel-purple-500 focus:border-transparent text-gray-900 dark:text-gray-100"
                  value={formData.issueDate}
                  onChange={handleChange}
                  required
                />
                {formData.issueDate && (
                  <small style={{ color: '#666', display: 'block', marginTop: '0.25rem' }}>
                    {formatDateDisplay(formData.issueDate)}
                  </small>
                )}
              </div>

              <div className="space-y-2" style={{ flex: 1 }}>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">{t('invoices.dueDate')}</label>
                <input
                  type="date"
                  name="dueDate"
                  className="w-full px-4 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-pastel-purple-500 focus:border-transparent text-gray-900 dark:text-gray-100"
                  value={formData.dueDate}
                  onChange={handleChange}
                />
                {formData.dueDate && (
                  <small style={{ color: '#666', display: 'block', marginTop: '0.25rem' }}>
                    {formatDateDisplay(formData.dueDate)}
                  </small>
                )}
              </div>

              <div className="space-y-2" style={{ flex: 1 }}>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">{t('invoices.serviceDate')}</label>
                <input
                  type="date"
                  name="serviceDate"
                  className="w-full px-4 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-pastel-purple-500 focus:border-transparent text-gray-900 dark:text-gray-100"
                  value={formData.serviceDate}
                  onChange={handleChange}
                />
                {formData.serviceDate && (
                  <small style={{ color: '#666', display: 'block', marginTop: '0.25rem' }}>
                    {formatDateDisplay(formData.serviceDate)}
                  </small>
                )}
              </div>
            </div>

            <div className="flex gap-2 mb-2">
              <div className="space-y-2" style={{ flex: 1 }}>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">{t('invoices.paymentMethod')}</label>
                <select
                  name="paymentMethod"
                  className="w-full px-4 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-pastel-purple-500 focus:border-transparent text-gray-900 dark:text-gray-100"
                  value={formData.paymentMethod}
                  onChange={handleChange}
                >
                  <option value="bank_transfer">{t('invoices.bankTransfer')}</option>
                  <option value="cash">{t('invoices.cash')}</option>
                  <option value="credit_card">{t('invoices.creditCard')}</option>
                  <option value="check">{t('invoices.check')}</option>
                </select>
              </div>

              <div className="space-y-2" style={{ flex: 1 }}>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">{t('invoices.bankAccount')}</label>
                <select
                  name="bankAccountId"
                  className="w-full px-4 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-pastel-purple-500 focus:border-transparent text-gray-900 dark:text-gray-100"
                  value={formData.bankAccountId}
                  onChange={handleChange}
                >
                  <option value="">{t('invoices.useDefaultBankAccount')}</option>
                  {bankAccounts.map(account => (
                    <option key={account.id} value={account.id}>
                      {account.name} - {account.accountNumber}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex gap-2 mb-2">
              <div className="space-y-2" style={{ flex: 1 }}>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">{t('invoices.taxDeductiblePercentage')} (%)*</label>
                <input
                  type="number"
                  step="0.01"
                  name="taxDeductiblePercentage"
                  className="w-full px-4 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-pastel-purple-500 focus:border-transparent text-gray-900 dark:text-gray-100"
                  value={formData.taxDeductiblePercentage}
                  onChange={handleChange}
                  required
                  min="0"
                  max="100"
                />
                <small className="text-gray-500 dark:text-gray-400">{t('invoices.taxDeductibleHelp')}</small>
              </div>
            </div>

            {/* Reverse Charge Checkbox */}
            <div className="flex items-center space-x-3 p-4 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg">
              <input
                type="checkbox"
                id="reverseCharge"
                name="reverseCharge"
                className="w-4 h-4 text-orange-600 bg-white border-gray-300 rounded focus:ring-orange-500 dark:focus:ring-orange-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                checked={formData.reverseCharge}
                onChange={(e) => setFormData({ ...formData, reverseCharge: e.target.checked })}
              />
              <label htmlFor="reverseCharge" className="flex-1">
                <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{t('invoices.reverseCharge')}</span>
                <p className="text-xs text-gray-600 dark:text-gray-400">{t('invoices.reverseChargeHelp')}</p>
              </label>
            </div>

            {/* Tax Rounding Checkbox */}
            <div className="flex items-center space-x-3 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
              <input
                type="checkbox"
                id="applyTaxRounding"
                name="applyTaxRounding"
                className="w-4 h-4 text-blue-600 bg-white border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                checked={formData.applyTaxRounding}
                onChange={(e) => {
                  const newValue = e.target.checked;
                  setFormData(prev => {
                    const totals = calculateTotalsFromItems(prev.items, newValue);
                    return {
                      ...prev,
                      applyTaxRounding: newValue,
                      ...totals
                    };
                  });
                }}
              />
              <label htmlFor="applyTaxRounding" className="flex-1">
                <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{t('invoices.applyTaxRounding')}</span>
                <p className="text-xs text-gray-600 dark:text-gray-400">{t('invoices.applyTaxRoundingHelp')}</p>
              </label>
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">{t('invoices.notes')}</label>
              <textarea
                name="notes"
                className="w-full px-4 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-pastel-purple-500 focus:border-transparent text-gray-900 dark:text-gray-100"
                value={formData.notes}
                onChange={handleChange}
                rows="2"
              />
            </div>

            <div className="flex gap-2">
              <button type="submit" className="bg-pastel-purple-600 hover:bg-pastel-purple-700 text-white font-medium py-2 px-6 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300">
                {editingId ? t('invoices.updateInvoice') : t('invoices.createInvoice')}
              </button>
              <button type="button" onClick={resetForm} className="bg-gray-600 hover:bg-gray-700 text-white font-medium py-2 px-6 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300">
                {t('common.cancel')}
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-md shadow-xl rounded-2xl border border-pastel-purple-200 dark:border-gray-700 p-6 mb-8 grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">{t('invoices.ownership')}</label>
          <select
            name="ownership"
            className="w-full px-4 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-pastel-purple-500 focus:border-transparent text-gray-900 dark:text-gray-100"
            value={filters.ownership}
            onChange={handleFilterChange}
          >
            {user?.role === 'super_admin' ? (
              <>
                <option value="all">{t('invoices.allUsersData')}</option>
                <option value="mine">{t('invoices.myInvoices')}</option>
              </>
            ) : (
              <>
                <option value="all">{t('invoices.allMineAndShared')}</option>
                <option value="mine">{t('invoices.myInvoices')}</option>
                <option value="shared">{t('invoices.sharedWithMe')}</option>
              </>
            )}
          </select>
        </div>

        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">{t('invoices.type')}</label>
          <select
            name="type"
            className="w-full px-4 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-pastel-purple-500 focus:border-transparent text-gray-900 dark:text-gray-100"
            value={filters.type}
            onChange={handleFilterChange}
          >
            <option value="">{t('invoices.all')}</option>
            <option value="incoming">{t('invoices.incoming')}</option>
            <option value="outgoing">{t('invoices.outgoing')}</option>
          </select>
        </div>

        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">{t('common.status')}</label>
          <select
            name="status"
            className="w-full px-4 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-pastel-purple-500 focus:border-transparent text-gray-900 dark:text-gray-100"
            value={filters.status}
            onChange={handleFilterChange}
          >
            <option value="">{t('invoices.all')}</option>
            <option value="draft">{t('invoices.draft')}</option>
            <option value="sent">{t('invoices.sent')}</option>
            <option value="paid">{t('invoices.paid')}</option>
            <option value="overdue">{t('invoices.overdue')}</option>
            <option value="cancelled">{t('invoices.cancelled')}</option>
          </select>
        </div>

        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">{t('common.name')}</label>
          <input
            type="text"
            name="companyName"
            className="w-full px-4 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-pastel-purple-500 focus:border-transparent text-gray-900 dark:text-gray-100"
            placeholder={t('invoices.searchByCompany')}
            value={filters.companyName}
            onChange={handleFilterChange}
          />
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="flex flex-col items-center space-y-4">
            <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-pastel-purple-500"></div>
            <p className="text-gray-600 dark:text-gray-400">{t('invoices.loadingInvoices')}</p>
          </div>
        </div>
      ) : (
        <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-md shadow-xl rounded-2xl border border-pastel-purple-200 dark:border-gray-700 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gradient-to-r from-pastel-purple-500 to-pastel-blue-500">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-bold text-white uppercase tracking-wider">{t('invoices.invoiceNumber')}</th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-white uppercase tracking-wider">{t('invoices.type')}</th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-white uppercase tracking-wider">{t('invoices.client')}</th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-white uppercase tracking-wider">{t('invoices.ownerCompany')}</th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-white uppercase tracking-wider">{t('common.amount')}</th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-white uppercase tracking-wider">{t('common.status')}</th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-white uppercase tracking-wider">{t('invoices.issueDate')}</th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-white uppercase tracking-wider">{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {invoices.length > 0 ? (
                  invoices.map((invoice) => {
                    const isOwner = invoice.userId === user?.id;
                    const hasEditPermission = invoice.sharedPermission === 'edit';
                    const canEditItem = canEdit && (isOwner || user?.role === 'super_admin' || hasEditPermission);
                    const companyName = invoice.user?.companySettings?.companyName || invoice.user?.username || 'N/A';

                    return (
                      <tr
                        key={invoice.id}
                        className={`hover:bg-pastel-purple-50 dark:hover:bg-gray-900/30 transition-colors ${
                          !isOwner ? 'bg-pastel-blue-50/50 dark:bg-pastel-blue-900/10' : ''
                        }`}
                      >
                        <td className="px-6 py-4 text-sm font-medium text-gray-900 dark:text-gray-100">
                          <div className="whitespace-nowrap">
                            {invoice.correctedInvoiceId && (
                              <span className="mr-1.5 text-xs font-semibold text-amber-700 dark:text-amber-400 bg-amber-100 dark:bg-amber-900/30 px-1.5 py-0.5 rounded">
                                {t('invoices.correctionBadge')}
                              </span>
                            )}
                            {invoice.invoiceNumber}
                            {!isOwner && (
                              <span
                                className="ml-2 text-xs text-gray-600 dark:text-gray-400 bg-gray-200 dark:bg-gray-700 px-2 py-1 rounded"
                                title={t('invoices.sharedInvoice')}
                              >
                                {t('invoices.shared')}
                              </span>
                            )}
                          </div>
                          {invoice.correctedInvoice && (
                            <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{t('invoices.correctionOf')}: {invoice.correctedInvoice.invoiceNumber}</div>
                          )}
                          {invoice.ksefNumber && (
                            <div className="text-xs text-gray-500 dark:text-gray-400 font-mono mt-0.5">{invoice.ksefNumber}</div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">{t(`invoices.${invoice.type}`)}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">{invoice.clientName}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                          {companyName}
                          {isOwner && (
                            <span className="ml-2 text-xs text-pastel-green-600 dark:text-pastel-green-400 font-bold">
                              ({t('invoices.you')})
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900 dark:text-gray-100">{formatCurrency(invoice.totalAmount, invoice.currency)}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm capitalize">
                          <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                            invoice.status === 'paid' ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200' :
                            invoice.status === 'sent' ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200' :
                            invoice.status === 'overdue' ? 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200' :
                            invoice.status === 'cancelled' ? 'bg-gray-100 dark:bg-gray-900/30 text-gray-800 dark:text-gray-200' :
                            'bg-gray-100 dark:bg-gray-900/30 text-gray-800 dark:text-gray-200'
                          }`}>
                            {invoice.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">{formatDateDisplay(invoice.issueDate)}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <div className="flex gap-2 flex-nowrap">
                            {invoice.attachmentPath ? (
                              <button
                                onClick={() => handleDownloadAttachment(invoice)}
                                className="bg-pastel-blue-600 hover:bg-pastel-blue-700 text-white font-medium py-1.5 px-4 rounded-lg shadow hover:shadow-lg transition-all duration-200 text-xs flex items-center gap-1"
                                title={t('invoices.downloadDocument')}
                              >
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                                {t('invoices.document')}
                              </button>
                            ) : (
                              <button
                                onClick={() => handleDownloadPDF(invoice)}
                                className="bg-pastel-green-600 hover:bg-pastel-green-700 text-white font-medium py-1.5 px-4 rounded-lg shadow hover:shadow-lg transition-all duration-200 text-xs"
                                title={t('invoices.downloadPDF')}
                              >
                                PDF
                              </button>
                            )}
                            {canEditItem && (
                              <>
                                <button
                                  onClick={() => !invoice.ksefNumber && handleExportToKsef(invoice.id)}
                                  className={`font-medium py-1.5 px-4 rounded-lg shadow transition-all duration-200 text-xs ${invoice.ksefNumber ? 'bg-gray-400 text-gray-200 cursor-not-allowed' : 'bg-amber-600 hover:bg-amber-700 text-white hover:shadow-lg disabled:opacity-50'}`}
                                  disabled={!!invoice.ksefNumber || ksefExportingId === invoice.id}
                                >
                                  {ksefExportingId === invoice.id ? t('common.loading') : t('invoices.exportToKsef')}
                                </button>
                                {invoice.ksefNumber && invoice.type === 'outgoing' ? (
                                  <button
                                    onClick={() => handleCreateCorrection(invoice)}
                                    className="bg-orange-600 hover:bg-orange-700 text-white font-medium py-1.5 px-4 rounded-lg shadow hover:shadow-lg transition-all duration-200 text-xs"
                                  >
                                    {t('invoices.createCorrection')}
                                  </button>
                                ) : (
                                  <button
                                    onClick={() => !invoice.ksefNumber && handleEdit(invoice)}
                                    className={`font-medium py-1.5 px-4 rounded-lg shadow transition-all duration-200 text-xs ${invoice.ksefNumber ? 'bg-gray-400 text-gray-200 cursor-not-allowed' : 'bg-pastel-purple-600 hover:bg-pastel-purple-700 text-white hover:shadow-lg'}`}
                                    disabled={!!invoice.ksefNumber}
                                  >
                                    {t('common.edit')}
                                  </button>
                                )}
                                <button
                                  onClick={() => handleDelete(invoice.id)}
                                  className="bg-red-600 hover:bg-red-700 text-white font-medium py-1.5 px-4 rounded-lg shadow hover:shadow-lg transition-all duration-200 text-xs"
                                >
                                  {t('common.delete')}
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="8" className="px-6 py-12 text-center">
                    <div className="flex flex-col items-center justify-center space-y-3">
                      <svg className="w-16 h-16 text-gray-400 dark:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <p className="text-gray-600 dark:text-gray-400 text-lg font-medium">{t('invoices.noInvoicesFound')}</p>
                      <p className="text-gray-500 dark:text-gray-500 text-sm">{t('invoices.tryAdjustingFilters')}</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default Invoices;
